/**
 * The parents' evening report — one child, the whole term.
 *
 * Read, not generated: every figure comes from the child's own sittings
 * (`GET /teacher/students/:id/term-report`), through the same tally as the
 * class console's "Students needing most attention", so the parent is told
 * what the teacher's shortlist says. It carries the two things the client
 * asked for — **pressure points** and an **overall band analysis** — and it
 * prints, because the copy that goes home is paper. (The page it replaced
 * offered a PDF from an endpoint retired with Puppeteer.)
 *
 * Scoped like every other teacher read, on the child's own class: a subject
 * teacher's copy says which subjects it covers, so a parent is never handed a
 * Science report believing it is the whole child.
 */
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import { BIN_LABEL, BIN_ORDER, binRamp } from '@/lib/bins';
import { BinGlyph } from '@/components/ds';
import { largestRemainder, type BinColor, type TermReport } from '@cwy/config/contracts';
import {
  BandRamp,
  DS_BIN,
  HeaderField,
  SheetFrame,
  fmtDate,
} from '@/components/reports/ActionPlanKit';

function Heading({ children, note }: { children: React.ReactNode; note?: string }) {
  return (
    <div className="mb-2">
      <h2 className="text-sm font-bold" style={{ color: 'var(--text-strong)' }}>
        {children}
      </h2>
      {note && (
        <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
          {note}
        </p>
      )}
    </div>
  );
}

function Band({ bin }: { bin: BinColor }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-fs-12 font-bold"
      style={{ color: binRamp(bin).text }}
    >
      <BinGlyph bin={DS_BIN[bin]} size={9} color="currentColor" title={null} />
      {BIN_LABEL[bin]}
    </span>
  );
}

/** Up, held or down — read along the ramp, Pink to Green. */
function movement(start: BinColor, end: BinColor): { word: string; tone: string } {
  const delta = BIN_ORDER.indexOf(start) - BIN_ORDER.indexOf(end);
  if (delta > 0) return { word: 'up', tone: 'var(--green-text)' };
  if (delta < 0) return { word: 'down', tone: 'var(--orange-text)' };
  return { word: 'held', tone: 'var(--ink-3)' };
}

const section = 'border-t px-5 py-4';

export function PTMReportPage() {
  const { studentId = '' } = useParams();
  const {
    data: r,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['term-report', studentId],
    enabled: Boolean(studentId),
    queryFn: () => apiGet<TermReport>(`/teacher/students/${studentId}/term-report`),
    retry: false,
  });

  if (isLoading) {
    return (
      <div
        className="h-64 animate-pulse rounded-2xl"
        style={{ background: 'var(--surface-sunk)' }}
      />
    );
  }
  if (isError || !r) {
    return (
      <p className="p-6 text-center text-sm" style={{ color: 'var(--ink-3)' }}>
        {(error as Error | null)?.message ?? 'No report for this student.'}
      </p>
    );
  }

  const total = BIN_ORDER.reduce((s, b) => s + r.binCounts[b], 0);
  const shares = largestRemainder(
    BIN_ORDER.map((b) => r.binCounts[b]),
    100,
  );
  const first = r.studentName.split(' ')[0];

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <Link
          to="/teacher/analytics"
          className="mr-auto text-xs font-semibold"
          style={{ color: 'var(--ink-3)' }}
        >
          ← Analytics
        </Link>
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded-full px-4 py-2 text-xs font-bold"
          style={{ background: 'var(--brand-forest)', color: 'var(--text-on-forest)' }}
        >
          Print for the parents
        </button>
      </div>

      <SheetFrame id="cwy-ptm-report">
        <header className="px-5 py-4" style={{ background: 'var(--paper-sunk)' }}>
          <p
            className="text-fs-10 font-bold uppercase tracking-[0.08em]"
            style={{ color: 'var(--ink-3)' }}
          >
            Parents’ evening · progress this term
          </p>
          <h1 className="text-xl font-bold" style={{ color: 'var(--text-strong)' }}>
            {r.studentName}
          </h1>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
            <HeaderField label="Class" value={`${r.board} ${r.gradeSection}`} />
            <HeaderField label="Roll" value={r.rollNumber} />
            <HeaderField
              label="Term"
              value={
                r.termStart && r.termEnd ? `${fmtDate(r.termStart)} – ${fmtDate(r.termEnd)}` : '—'
              }
            />
            <HeaderField label="Papers sat" value={String(r.papersSat)} />
            <HeaderField label="Average" value={`${r.averagePercent}%`} />
          </div>
          {r.scopedTo && (
            <p className="mt-3 text-xs font-semibold" style={{ color: 'var(--orange-text)' }}>
              This copy covers {r.scopedTo.join(', ') || 'no subject'} only — the subjects you teach{' '}
              {first}. The school admin’s copy covers every subject.
            </p>
          )}
        </header>

        <div className={section} style={{ borderColor: 'var(--border-subtle)' }}>
          <Heading
            note={`Every question ${first} reached, across all ${r.papersSat} papers — how each mark was earned, not only whether.`}
          >
            Overall band analysis
          </Heading>
          <BandRamp counts={r.binCounts} height={12} />
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {BIN_ORDER.map((bin, i) => (
              <span
                key={bin}
                className="inline-flex items-center gap-1 text-xs font-bold"
                style={{ color: binRamp(bin).text }}
                title={`${r.binCounts[bin]} of ${total} questions`}
              >
                <BinGlyph bin={DS_BIN[bin]} size={9} color="currentColor" title={null} />
                {BIN_LABEL[bin]} {shares[i]}%
              </span>
            ))}
          </div>
        </div>

        <div className={section} style={{ borderColor: 'var(--border-subtle)' }}>
          <Heading note="First paper to latest, one subject at a time — a Maths score and an English score are not one trend.">
            Subject by subject
          </Heading>
          <table className="w-full text-left text-sm">
            <thead>
              <tr
                className="text-fs-10 uppercase tracking-[0.08em]"
                style={{ color: 'var(--ink-3)' }}
              >
                <th className="pb-1 font-bold">Subject</th>
                <th className="pb-1 font-bold">Papers</th>
                <th className="pb-1 font-bold">Average</th>
                <th className="pb-1 font-bold">First → latest</th>
                <th className="w-2/5 pb-1 font-bold">Bands</th>
              </tr>
            </thead>
            <tbody>
              {r.bySubject.map((s) => (
                <tr key={s.subject} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td className="py-2 font-bold" style={{ color: 'var(--text-body)' }}>
                    {s.subject}
                  </td>
                  <td className="py-2" data-numeric="">
                    {s.papers}
                  </td>
                  <td className="py-2 font-bold" data-numeric="">
                    {s.averagePercent}%
                  </td>
                  <td className="py-2" data-numeric="">
                    {s.firstPercent}% → {s.lastPercent}%
                  </td>
                  <td className="py-2">
                    <BandRamp counts={s.binCounts} height={8} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={section} style={{ borderColor: 'var(--border-subtle)' }}>
          <Heading note="What keeps coming back, and where the clock runs out. A pattern, not one bad morning.">
            Pressure points
          </Heading>
          {r.pressurePoints.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
              Nothing recurring. No topic has gone wrong in most of the papers that asked it, and
              none has run out of time more often than not.
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {r.pressurePoints.map((p) => (
                <li key={`${p.kind}-${p.topicName}`} className="flex items-baseline gap-2 text-sm">
                  {/* Recurring is the re-teach band; running out of time is the
                      slow one — the glyph says which kind before the words do. */}
                  <span
                    className="flex-none"
                    style={{ color: binRamp(p.kind === 'time' ? 'PURPLE' : 'ORANGE').text }}
                  >
                    <BinGlyph
                      bin={DS_BIN[p.kind === 'time' ? 'PURPLE' : 'ORANGE']}
                      size={9}
                      color="currentColor"
                      title={p.kind === 'time' ? 'Time pressure' : 'Keeps coming back'}
                    />
                  </span>
                  <span>
                    <strong style={{ color: 'var(--text-strong)' }}>{p.topicName}</strong>{' '}
                    <span style={{ color: 'var(--ink-3)' }}>· {p.subject} ·</span>{' '}
                    <span style={{ color: 'var(--ink-2)' }}>{p.detail}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={section} style={{ borderColor: 'var(--border-subtle)' }}>
          <Heading note="Each topic on the first paper that asked it, and on the latest. Topics met only once are left out — one paper is a reading, not a movement.">
            How each topic moved
          </Heading>
          <table className="w-full text-left text-sm">
            <tbody>
              {r.progress.map((t) => {
                const move = movement(t.start, t.end);
                return (
                  <tr key={t.topicId} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td className="py-1.5 font-semibold" style={{ color: 'var(--text-body)' }}>
                      {t.topicName}
                    </td>
                    <td className="py-1.5 text-xs" style={{ color: 'var(--ink-3)' }}>
                      {t.subject}
                    </td>
                    <td className="py-1.5">
                      <Band bin={t.start} /> <span style={{ color: 'var(--ink-3)' }}>→</span>{' '}
                      <Band bin={t.end} />
                    </td>
                    <td className="py-1.5 text-xs font-bold" style={{ color: move.tone }}>
                      {move.word}
                    </td>
                    <td
                      className="py-1.5 text-xs"
                      data-numeric=""
                      style={{ color: 'var(--ink-3)' }}
                    >
                      {t.papers} papers
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div
          className={`${section} grid gap-4 sm:grid-cols-2`}
          style={{ borderColor: 'var(--border-subtle)' }}
        >
          <div>
            <Heading>Strengths</Heading>
            <ul className="list-inside list-disc text-sm" style={{ color: 'var(--ink-2)' }}>
              {r.strengths.length ? (
                r.strengths.map((s) => (
                  <li key={s.topicId}>
                    {s.topicName} <span style={{ color: 'var(--ink-3)' }}>· {s.subject}</span>
                  </li>
                ))
              ) : (
                <li>Building steadily — no topic has settled in Green yet.</li>
              )}
            </ul>
          </div>
          <div>
            <Heading>Growth areas</Heading>
            <ul className="list-inside list-disc text-sm" style={{ color: 'var(--ink-2)' }}>
              {r.growth.length ? (
                r.growth.map((g) => (
                  <li key={g.topicId}>
                    {g.topicName}{' '}
                    <span style={{ color: 'var(--ink-3)' }}>
                      · {g.subject} · {g.questions} to rebuild
                    </span>
                  </li>
                ))
              ) : (
                <li>Nothing waiting on the teacher this term.</li>
              )}
            </ul>
          </div>
        </div>

        <div className={section} style={{ borderColor: 'var(--border-subtle)' }}>
          <Heading>Next steps</Heading>
          <ol className="list-inside list-decimal text-sm" style={{ color: 'var(--ink-2)' }}>
            {r.nextSteps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
        </div>

        <div
          className={section}
          style={{ borderColor: 'var(--border-subtle)', background: 'var(--paper-sunk)' }}
        >
          <p className="text-xs" style={{ color: 'var(--ink-2)' }}>
            <strong>Effort log</strong> · {r.effort.totalHours} hours on task this term ·{' '}
            {r.effort.avgSessionMin} minutes a sitting · {r.effort.daysPerWeek} days a week
          </p>
        </div>
      </SheetFrame>
    </div>
  );
}
